// ============================================================================
// @gs/ui/branding/deriveBrandScale — échelle de marque dérivée (ADR-059 §7.1)
// ============================================================================
// Une seule source de vérité couleur par tenant : le hex `color_primary` de
// ug.CompanyBranding. L'échelle 50..900 est DÉRIVÉE ici (jamais stockée), en
// canaux RGB bruts ("R G B") pour préserver l'alpha Tailwind
// (`bg-brand-500/8`, `ring-brand-500/20` → rgb(var(--brand-N-rgb) / <alpha>)).
//
// Ratios FIGÉS (validés visuellement par David sur le mock 2026-06-10,
// BLITZ-MASTER-SHELL-V2-THEMING) — ne pas redériver :
//   50..400 = mix linéaire par canal vers BLANC [255,255,255]
//   500     = hex brut
//   600..900 = mix linéaire par canal vers NOIR  [20,12,10] (noir chaud)
// ============================================================================

const WHITE: readonly [number, number, number] = [255, 255, 255];
const BLACK: readonly [number, number, number] = [20, 12, 10];

/** Ratios figés : étape → { vers, poids } (poids = fraction du chemin vers la cible). */
const STEPS = {
  50:  { target: WHITE, t: 0.95 },
  100: { target: WHITE, t: 0.88 },
  200: { target: WHITE, t: 0.74 },
  300: { target: WHITE, t: 0.55 },
  400: { target: WHITE, t: 0.30 },
  500: { target: WHITE, t: 0 }, // hex brut
  600: { target: BLACK, t: 0.12 },
  700: { target: BLACK, t: 0.28 },
  800: { target: BLACK, t: 0.45 },
  900: { target: BLACK, t: 0.62 },
} as const;

export type BrandScaleStep = keyof typeof STEPS;
export type BrandScale = Record<BrandScaleStep, string>;

/**
 * Convertit un hex CSS (#abc ou #aabbcc, case-insensible) en canaux RGB bruts
 * `"R G B"` (format des variables `--brand-N-rgb`). Throw sur hex invalide —
 * la donnée vient de la BD, un format inattendu doit échouer bruyamment côté
 * appelant (le BrandingProvider catch et garde le fallback :root).
 */
export function hexToRgbChannels(hex: string): string {
  const [r, g, b] = parseHex(hex);
  return `${r} ${g} ${b}`;
}

function parseHex(hex: string): [number, number, number] {
  if (typeof hex !== 'string') throw new Error(`Hex de marque invalide: ${String(hex)}`);
  const raw = hex.trim().replace(/^#/, '');
  const six = raw.length === 3 ? raw.split('').map((c) => c + c).join('') : raw;
  if (!/^[0-9a-fA-F]{6}$/.test(six)) throw new Error(`Hex de marque invalide: ${hex}`);
  return [
    parseInt(six.slice(0, 2), 16),
    parseInt(six.slice(2, 4), 16),
    parseInt(six.slice(4, 6), 16),
  ];
}

function mixChannel(from: number, to: number, t: number): number {
  return Math.round(from + (to - from) * t);
}

/**
 * Dérive l'échelle complète 50..900 d'un hex de marque, en canaux `"R G B"`.
 * Usage : `deriveBrandScale('#d51224')[500]` → `"213 18 36"`.
 */
export function deriveBrandScale(hex: string): BrandScale {
  const base = parseHex(hex);
  const scale = {} as BrandScale;
  for (const [step, { target, t }] of Object.entries(STEPS)) {
    const [r, g, b] = [0, 1, 2].map((i) => mixChannel(base[i] as number, target[i] as number, t));
    scale[Number(step) as BrandScaleStep] = `${r} ${g} ${b}`;
  }
  return scale;
}

/** Étapes de l'échelle, ordonnées (pour itérer côté BrandingProvider). */
export const BRAND_SCALE_STEPS = Object.keys(STEPS).map(Number) as BrandScaleStep[];
